/**
 * CajaAdminSalesGateSettings — PIN de admin/dueño cada N ventas (independiente del tope de efectivo).
 *
 * Persiste en la misma clave que el tope (`cashDrawerSecurityConfig`) pero el usuario puede
 * activar solo esta opción o solo el tope, sin acoplarlos.
 */
import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { FloatingLabelInput } from "@/components/ui/FloatingLabelInput";
import { SwitchRow } from "@/components/ui/Switch";
import { normalizeMoneyInput } from "@/lib/utils/formatters";
import {
  loadCashDrawerSecurityConfig,
  normalizeCashDrawerSecurity,
  persistCashDrawerSecurityConfig,
} from "@/features/caja/lib/cashDrawerSecurityConfig";

const SECTION_LABEL =
  "text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2 px-0.5";

const EXPAND_GRID =
  "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none";

export function CajaAdminSalesGateSettings() {
  const businessId = useAuthStore((s) => s.user?.businessId);
  const [hydrated, setHydrated] = useState(false);
  const [adminEnabled, setAdminEnabled] = useState(false);
  const [everyStr, setEveryStr] = useState("10");

  useEffect(() => {
    setHydrated(false);
    const c = normalizeCashDrawerSecurity(
      loadCashDrawerSecurityConfig(businessId),
    );
    setAdminEnabled(c.adminSalesGateEnabled);
    setEveryStr(String(c.adminSalesGateEvery || 10));
    setHydrated(true);
  }, [businessId]);

  useEffect(() => {
    const onExternal = () => {
      const c = normalizeCashDrawerSecurity(
        loadCashDrawerSecurityConfig(businessId),
      );
      setAdminEnabled(c.adminSalesGateEnabled);
      setEveryStr(String(c.adminSalesGateEvery || 10));
    };
    window.addEventListener("altenot:cash-drawer-security", onExternal);
    return () =>
      window.removeEventListener("altenot:cash-drawer-security", onExternal);
  }, [businessId]);

  useEffect(() => {
    if (!hydrated) return;
    const id = window.setTimeout(() => {
      const full = normalizeCashDrawerSecurity(
        loadCashDrawerSecurityConfig(businessId),
      );
      const every = Number(normalizeMoneyInput(everyStr, 0)) || 10;
      const next = normalizeCashDrawerSecurity({
        ...full,
        adminSalesGateEnabled: adminEnabled,
        adminSalesGateEvery: every,
      });
      const prev = normalizeCashDrawerSecurity(
        loadCashDrawerSecurityConfig(businessId),
      );
      if (JSON.stringify(prev) === JSON.stringify(next)) return;
      persistCashDrawerSecurityConfig(businessId, next);
      setEveryStr(String(next.adminSalesGateEvery));
    }, 420);
    return () => window.clearTimeout(id);
  }, [businessId, hydrated, adminEnabled, everyStr]);

  return (
    <div className="space-y-0">
      <SwitchRow
        size="sm"
        icon={
          <ShieldCheck
            size={16}
            className="text-slate-600 dark:text-slate-300"
          />
        }
        label="Pedir PIN admin cada N ventas"
        description="A cada N ventas: COBRAR exige PIN admin o dueño."
        checked={adminEnabled}
        onChange={setAdminEnabled}
      />

      <div
        className={`${EXPAND_GRID} ${adminEnabled ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
        aria-hidden={!adminEnabled}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="pt-3">
            <div>
              <p className={SECTION_LABEL}>Frecuencia</p>
              <FloatingLabelInput
                label="N ventas"
                hint="2 a 99"
                disabled={!adminEnabled}
                value={everyStr}
                onChange={(e) =>
                  setEveryStr(normalizeMoneyInput(e.target.value, 0))
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
